import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Search, Grid3X3, List, SlidersHorizontal, ChevronLeft, ChevronRight,
  ShoppingBag, Tag, Building2, Globe2, ArrowUpDown, ExternalLink,
  Package, Store, X, Eye, Mail, Phone, MessageCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  product: 'Product',
  service: 'Service',
  bundle: 'Bundle',
  subscription: 'Subscription',
  other: 'Other',
};

const CATEGORY_COLORS = {
  product: 'bg-blue-50 text-blue-700 border-blue-200',
  service: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  bundle: 'bg-amber-50 text-amber-700 border-amber-200',
  subscription: 'bg-violet-50 text-violet-700 border-violet-200',
  other: 'bg-gray-50 text-gray-600 border-gray-200',
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'name_az', label: 'Name: A-Z' },
  { value: 'name_za', label: 'Name: Z-A' },
];

function formatPrice(price, currency = 'USD', unit = 'each') {
  const formatted = currency === 'USD' ? `$${price.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `${price.toLocaleString()} ${currency}`;
  if (!unit || unit === 'each') return formatted;
  return `${formatted} / ${unit}`;
}

export default function MarketplacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_products: 0, total_sellers: 0, total_categories: 0 });
  const [filterOptions, setFilterOptions] = useState({ categories: [], industries: [], countries: [] });

  // State from URL params
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [industry, setIndustry] = useState(searchParams.get('industry') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [view, setView] = useState(searchParams.get('view') || 'grid');
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(search);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      if (industry) params.set('industry', industry);
      if (country) params.set('country', country);
      params.set('sort_by', sortBy);
      params.set('page', String(page));
      params.set('limit', '24');

      const res = await axios.get(`${API}/api/marketplace/listings?${params}`);
      setListings(res.data.listings);
      setTotal(res.data.total);
      setPages(res.data.pages);
      setFilterOptions({
        categories: res.data.categories || [],
        industries: res.data.industries || [],
        countries: res.data.countries || [],
      });
    } catch (e) {
      console.error('Marketplace fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [search, category, industry, country, sortBy, page]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Stats error:', e);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchListings(); }, [fetchListings]);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('q', search);
    if (category) params.set('category', category);
    if (industry) params.set('industry', industry);
    if (country) params.set('country', country);
    if (sortBy !== 'newest') params.set('sort', sortBy);
    if (page > 1) params.set('page', String(page));
    if (view !== 'grid') params.set('view', view);
    setSearchParams(params, { replace: true });
  }, [search, category, industry, country, sortBy, page, view, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setCategory('');
    setIndustry('');
    setCountry('');
    setSortBy('newest');
    setPage(1);
  };

  const hasFilters = search || category || industry || country || sortBy !== 'newest';

  const handleViewProduct = async (listing) => {
    if (listing.share_token) {
      navigate(`/showcase/${listing.share_token}`);
    } else {
      // No share token — fetch full detail and show modal
      setDetailLoading(true);
      setSelectedProduct(listing);
      try {
        const res = await axios.get(`${API}/api/marketplace/listings/${listing.id}`);
        if (!res.data.error) setSelectedProduct(res.data);
      } catch (e) { /* keep partial data */ }
      finally { setDetailLoading(false); }
    }
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="marketplace-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-emerald-900 flex items-center justify-center">
            <Store className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary" data-testid="marketplace-title">
              Konnect Marketplace<PageInfoTooltip pageKey="marketplace" />
            </h1>
            <div className="flex items-center gap-4 text-xs text-gray-400 mt-0.5">
              <span className="flex items-center gap-1"><Package className="h-3 w-3 text-emerald-500" />{stats.total_products} Products</span>
              <span className="flex items-center gap-1"><Building2 className="h-3 w-3 text-blue-500" />{stats.total_sellers} Sellers</span>
              <span className="flex items-center gap-1"><Tag className="h-3 w-3 text-amber-500" />{stats.total_categories} Categories</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch}>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search products, services, tags..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-9 h-10"
              data-testid="marketplace-search-input"
            />
          </div>
          <Button type="submit" className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="marketplace-search-btn">
            Search
          </Button>
        </div>
      </form>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-slate-500" data-testid="marketplace-result-count">
            {loading ? 'Loading...' : `${total} result${total !== 1 ? 's' : ''}`}
          </p>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 hover:text-red-500 gap-1" data-testid="marketplace-clear-filters">
                <X className="h-3 w-3" />Clear filters
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={cn("gap-1.5 text-xs rounded-lg", showFilters && "bg-slate-100")}
              data-testid="marketplace-toggle-filters"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />Filters
            </Button>

            <select
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs bg-white"
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setPage(1); }}
              data-testid="marketplace-sort"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            <div className="flex border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setView('grid')}
                className={cn("p-1.5 transition-colors", view === 'grid' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50')}
                data-testid="marketplace-view-grid"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setView('list')}
                className={cn("p-1.5 transition-colors", view === 'list' ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50')}
                data-testid="marketplace-view-list"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <Card className="mb-5 border-slate-200 bg-white animate-fadeIn" data-testid="marketplace-filter-panel">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Category</label>
                  <select className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" value={category} onChange={e => { setCategory(e.target.value); setPage(1); }} data-testid="marketplace-filter-category">
                    <option value="">All Categories</option>
                    {filterOptions.categories.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Industry</label>
                  <select className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" value={industry} onChange={e => { setIndustry(e.target.value); setPage(1); }} data-testid="marketplace-filter-industry">
                    <option value="">All Industries</option>
                    {filterOptions.industries.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Country</label>
                  <select className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white" value={country} onChange={e => { setCountry(e.target.value); setPage(1); }} data-testid="marketplace-filter-country">
                    <option value="">All Countries</option>
                    {filterOptions.countries.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Filter Tags */}
        {hasFilters && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="marketplace-active-filters">
            {search && (
              <Badge variant="outline" className="gap-1 text-xs bg-white cursor-pointer hover:bg-red-50" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                Search: "{search}" <X className="h-3 w-3" />
              </Badge>
            )}
            {category && (
              <Badge variant="outline" className="gap-1 text-xs bg-white cursor-pointer hover:bg-red-50" onClick={() => { setCategory(''); setPage(1); }}>
                {CATEGORY_LABELS[category] || category} <X className="h-3 w-3" />
              </Badge>
            )}
            {industry && (
              <Badge variant="outline" className="gap-1 text-xs bg-white cursor-pointer hover:bg-red-50" onClick={() => { setIndustry(''); setPage(1); }}>
                {industry} <X className="h-3 w-3" />
              </Badge>
            )}
            {country && (
              <Badge variant="outline" className="gap-1 text-xs bg-white cursor-pointer hover:bg-red-50" onClick={() => { setCountry(''); setPage(1); }}>
                {country} <X className="h-3 w-3" />
              </Badge>
            )}
          </div>
        )}

        {/* Listings */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="bg-white border-slate-200 animate-pulse">
                <div className="h-44 bg-slate-100 rounded-t-lg" />
                <CardContent className="p-4 space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                  <div className="h-5 bg-slate-100 rounded w-1/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20" data-testid="marketplace-empty">
            <ShoppingBag className="h-14 w-14 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No products found</h3>
            <p className="text-sm text-slate-500 mt-1">Try adjusting your search or filters</p>
            {hasFilters && (
              <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>Clear all filters</Button>
            )}
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="marketplace-grid">
            {listings.map(item => (
              <ProductCard key={item.id} item={item} onView={handleViewProduct} />
            ))}
          </div>
        ) : (
          <div className="space-y-3" data-testid="marketplace-list">
            {listings.map(item => (
              <ProductListItem key={item.id} item={item} onView={handleViewProduct} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8" data-testid="marketplace-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="gap-1"
              data-testid="marketplace-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />Prev
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                let pageNum;
                if (pages <= 7) {
                  pageNum = i + 1;
                } else if (page <= 4) {
                  pageNum = i + 1;
                } else if (page >= pages - 3) {
                  pageNum = pages - 6 + i;
                } else {
                  pageNum = page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cn(
                      "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                      page === pageNum ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pages}
              onClick={() => setPage(p => p + 1)}
              className="gap-1"
              data-testid="marketplace-next-page"
            >
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Back to Konnect */}

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => { if (!open) setSelectedProduct(null); }}>
        <DialogContent className="max-w-lg sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="marketplace-product-detail">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-slate-800">{selectedProduct.name}</DialogTitle>
              </DialogHeader>

              {/* Image Gallery */}
              {selectedProduct.image_urls?.length > 0 && (
                <div className="relative h-56 sm:h-72 bg-slate-50 rounded-lg overflow-hidden -mx-2">
                  <img src={selectedProduct.image_urls[0]} alt={selectedProduct.name} className="w-full h-full object-cover" />
                  {selectedProduct.image_urls.length > 1 && (
                    <div className="absolute bottom-2 left-2 flex gap-1.5">
                      {selectedProduct.image_urls.slice(1, 5).map((url, i) => (
                        <div key={i} className="w-12 h-12 rounded-md overflow-hidden border-2 border-white shadow-sm">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {/* Price + Category */}
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold text-slate-900">{formatPrice(selectedProduct.price, selectedProduct.currency, selectedProduct.unit)}</p>
                  <Badge className={cn("text-xs border", CATEGORY_COLORS[selectedProduct.category] || CATEGORY_COLORS.other)}>
                    {CATEGORY_LABELS[selectedProduct.category] || selectedProduct.category}
                  </Badge>
                </div>

                {/* Description */}
                {selectedProduct.description && (
                  <p className="text-sm text-slate-600 leading-relaxed">{selectedProduct.description}</p>
                )}

                {/* Tags */}
                {selectedProduct.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedProduct.tags.map(tag => (
                      <Badge key={tag} variant="outline" className="text-[10px] text-slate-500">{tag}</Badge>
                    ))}
                  </div>
                )}

                {/* Seller Info */}
                {selectedProduct.seller && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-xs font-medium text-slate-400 mb-2">Seller</p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-white">{(selectedProduct.seller.business_name || '?').charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{selectedProduct.seller.business_name || 'Unknown Seller'}</p>
                        <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-0.5">
                          {selectedProduct.seller.industry && <span>{selectedProduct.seller.industry}</span>}
                          {selectedProduct.seller.country && <span className="capitalize">{selectedProduct.seller.country}</span>}
                        </div>
                      </div>
                    </div>
                    {/* Contact options */}
                    {(selectedProduct.seller.email || selectedProduct.seller.phone) && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200">
                        {selectedProduct.seller.email && (
                          <a href={`mailto:${selectedProduct.seller.email}`} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-emerald-600 transition-colors">
                            <Mail className="h-3.5 w-3.5" />Email Seller
                          </a>
                        )}
                        {selectedProduct.seller.phone && (
                          <a href={`tel:${selectedProduct.seller.phone}`} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-emerald-600 transition-colors">
                            <Phone className="h-3.5 w-3.5" />Call
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Video */}
                {selectedProduct.video_url && (
                  <a href={selectedProduct.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors">
                    <ExternalLink className="h-4 w-4" />Watch Product Video
                  </a>
                )}

                {/* Contact Seller Form */}
                <MarketplaceInquiryForm productId={selectedProduct.id} productName={selectedProduct.name} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Product Card (Grid View) ── */
function ProductCard({ item, onView }) {
  const hasImage = item.image_urls?.length > 0;
  const catStyle = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;

  return (
    <Card
      className="bg-white border-slate-200 overflow-hidden group hover:shadow-lg hover:border-slate-300 transition-all duration-200 cursor-pointer"
      onClick={() => onView(item)}
      data-testid={`marketplace-product-card-${item.id}`}
    >
      {/* Image */}
      <div className="relative h-44 bg-slate-50 overflow-hidden">
        {hasImage ? (
          <img
            src={item.image_urls[0]}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-12 w-12 text-slate-300" />
          </div>
        )}
        <Badge className={cn("absolute top-2.5 left-2.5 text-[10px] border", catStyle)}>
          {CATEGORY_LABELS[item.category] || item.category}
        </Badge>
        {item.share_token && (
          <div className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="p-1.5 bg-white/90 rounded-lg shadow-sm backdrop-blur-sm">
              <Eye className="h-3.5 w-3.5 text-slate-600" />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <CardContent className="p-3.5">
        <h3 className="text-sm font-semibold text-slate-800 line-clamp-1 group-hover:text-emerald-700 transition-colors">{item.name}</h3>
        {item.description && (
          <p className="text-[11px] text-slate-500 line-clamp-2 mt-1 leading-relaxed">{item.description}</p>
        )}

        <div className="flex items-center justify-between mt-3">
          <p className="text-base font-bold text-slate-900">{formatPrice(item.price, item.currency, item.unit)}</p>
          {item.tags?.length > 0 && (
            <Badge variant="outline" className="text-[9px] text-slate-400 border-slate-200">{item.tags[0]}</Badge>
          )}
        </div>

        {/* Seller */}
        {item.seller?.business_name && (
          <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-slate-100">
            <div className="w-5 h-5 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
              <span className="text-[8px] font-bold text-white">{item.seller.business_name.charAt(0)}</span>
            </div>
            <span className="text-[11px] text-slate-500 truncate">{item.seller.business_name}</span>
            {item.seller.country && (
              <span className="text-[10px] text-slate-400 capitalize ml-auto flex-shrink-0">{item.seller.country}</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Product List Item (List View) ── */
function ProductListItem({ item, onView }) {
  const hasImage = item.image_urls?.length > 0;
  const catStyle = CATEGORY_COLORS[item.category] || CATEGORY_COLORS.other;

  return (
    <Card
      className="bg-white border-slate-200 overflow-hidden group hover:shadow-md hover:border-slate-300 transition-all duration-200 cursor-pointer"
      onClick={() => onView(item)}
      data-testid={`marketplace-product-list-${item.id}`}
    >
      <div className="flex">
        {/* Image */}
        <div className="w-32 sm:w-40 h-28 sm:h-32 bg-slate-50 overflow-hidden flex-shrink-0">
          {hasImage ? (
            <img src={item.image_urls[0]} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-3.5 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start gap-2">
              <h3 className="text-sm font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors truncate flex-1">{item.name}</h3>
              <Badge className={cn("text-[10px] border flex-shrink-0", catStyle)}>{CATEGORY_LABELS[item.category] || item.category}</Badge>
            </div>
            {item.description && (
              <p className="text-[11px] text-slate-500 line-clamp-2 mt-1">{item.description}</p>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-base font-bold text-slate-900">{formatPrice(item.price, item.currency, item.unit)}</p>
            <div className="flex items-center gap-2">
              {item.seller?.business_name && (
                <span className="text-[11px] text-slate-500">{item.seller.business_name}</span>
              )}
              {item.seller?.country && (
                <span className="text-[10px] text-slate-400 capitalize">{item.seller.country}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}


/* ── Marketplace Inquiry Form ── */
function MarketplaceInquiryForm({ productId, productName }) {
  const [form, setForm] = useState({ name: '', contact: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/marketplace/listings/${productId}/inquiry`, {
        name: form.name,
        contact: form.contact,
        message: form.message,
      });
      setSent(true);
      toast.success('Inquiry sent to seller!');
    } catch {
      toast.error('Failed to send inquiry');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 text-center" data-testid="marketplace-inquiry-sent">
        <p className="text-sm font-medium text-emerald-700">Inquiry sent!</p>
        <p className="text-xs text-emerald-600 mt-0.5">The seller will get back to you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2.5" data-testid="marketplace-inquiry-form">
      <p className="text-xs font-medium text-slate-600">Contact Seller about {productName}</p>
      <Input placeholder="Your name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="h-8 text-sm" required data-testid="marketplace-inquiry-name" />
      <Input placeholder="Email or phone" value={form.contact} onChange={e => setForm(p => ({ ...p, contact: e.target.value }))} className="h-8 text-sm" required data-testid="marketplace-inquiry-contact" />
      <Textarea placeholder="Your message (optional)" value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} rows={2} className="text-sm" data-testid="marketplace-inquiry-message" />
      <Button type="submit" disabled={sending} className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="marketplace-inquiry-submit">
        {sending ? 'Sending...' : 'Send Inquiry'}
      </Button>
    </form>
  );
}
